import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  const igrejaId = localStorage.getItem('ebd_igreja_id');
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Para super_admin, não enviar X-Igreja-ID se for 'all' (acesso global)
  // Para outros usuários, sempre enviar se houver
  if (igrejaId && igrejaId !== 'all') {
    headers['X-Igreja-ID'] = igrejaId;
  }
  
  return headers;
};

const SortableTh = ({ field, label, current, dir, onSort, align = 'left', className = '' }) => {
  const isActive = current === field;
  return (
    <th
      onClick={() => onSort(field)}
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:bg-gray-100 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && <span className="text-[10px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
};

const GerenciarIgrejas = ({ setCurrentView, currentUser, onLogout }) => {
  const [igrejas, setIgrejas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingIgreja, setEditingIgreja] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  
  // Estados para Backup/Restore
  const [backupLoading, setBackupLoading] = useState({});
  const [restoreIgreja, setRestoreIgreja] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Estados para Backup/Restore Global
  const [globalBackupLoading, setGlobalBackupLoading] = useState(false);
  const [globalBackupEmailLoading, setGlobalBackupEmailLoading] = useState(false);
  const [showGlobalRestore, setShowGlobalRestore] = useState(false);
  const [globalRestoreFile, setGlobalRestoreFile] = useState(null);
  const [globalRestoreLoading, setGlobalRestoreLoading] = useState(false);
  const [globalRestorePreview, setGlobalRestorePreview] = useState(null);
  const [globalRestoreSelected, setGlobalRestoreSelected] = useState({});
  
  // Estados para Upload de Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Estados para Cleanup
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupStats, setCleanupStats] = useState(null);

  // Filtro de status (default = ativas_trial: oculta desativadas/em atraso)
  const [filtroStatus, setFiltroStatus] = useState('ativas_trial');
  const [busca, setBusca] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1, limit: 50 });
  const [sortField, setSortField] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');

  const PLANOS_OPTIONS = [
    { value: 'trial', label: 'Trial (45 dias)' },
    { value: 'basico', label: 'Registrado (50 alunos / 12 turmas)' },
    { value: 'intermediario', label: 'Intermediario (150 alunos / 20 turmas)' },
    { value: 'avancado', label: 'Avancado (200 alunos / 35 turmas)' },
    { value: 'ilimitado', label: 'Ilimitado (400 alunos / 40 turmas)' },
    { value: 'free', label: 'Free (sem limites)' },
  ];

  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    presidente: '',
    pastor: '',
    superintendente_ebd: '',
    endereco: '',
    telefone: '',
    email: '',
    plano: 'trial',
    valor_mensal: 0,
    dia_vencimento: '',
    admin_nome: '',
    admin_email: '',
    admin_senha: ''
  });

  useEffect(() => {
    loadIgrejas();
    // eslint-disable-next-line
  }, [filtroStatus, busca, page, sortField, sortDir]);

  const loadIgrejas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: filtroStatus,
        search: busca,
        page: String(page),
        limit: '50',
        sort: sortField,
        sort_dir: sortDir,
      });
      const response = await axios.get(`${API}/igrejas/listar?${params}`, { headers: getAuthHeaders() });
      const data = response.data || {};
      setIgrejas(data.items || []);
      setPageInfo({ total: data.total || 0, pages: data.pages || 1, limit: data.limit || 50 });
    } catch (error) {
      console.error('Erro ao carregar igrejas:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      if (error.response?.status === 401) {
        showMessage('error', 'Sessão expirada. Faça login novamente.');
      } else if (error.response?.status === 403) {
        showMessage('error', 'Sem permissão para acessar igrejas.');
      } else {
        showMessage('error', `Erro ao carregar igrejas: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Limpar campos vazios (converter string vazia para null)
      const cleanData = { ...formData };
      // Converter valor_mensal para número
      if (cleanData.valor_mensal) {
        cleanData.valor_mensal = parseFloat(cleanData.valor_mensal) || 0;
      }
      // Converter dia_vencimento para número ou null
      if (cleanData.dia_vencimento) {
        cleanData.dia_vencimento = parseInt(cleanData.dia_vencimento) || null;
      } else {
        cleanData.dia_vencimento = null;
      }
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') {
          // Manter campos obrigatórios, mas limpar opcionais
          if (['nome', 'presidente', 'pastor', 'superintendente_ebd', 'plano', 'valor_mensal', 'admin_nome', 'admin_email', 'admin_senha'].includes(key)) {
            // Campos obrigatórios - manter vazio se necessário
          } else {
            cleanData[key] = null;
          }
        }
      });

      if (editingIgreja) {
        // Editar igreja existente - remover campos de admin
        const { admin_nome, admin_email, admin_senha, ...updateData } = cleanData;
        await axios.put(
          `${API}/igrejas/${editingIgreja.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        showMessage('success', 'Igreja atualizada com sucesso!');
      } else {
        // Criar nova igreja - enviar todos os dados incluindo admin
        await axios.post(`${API}/igrejas`, cleanData, { headers: getAuthHeaders() });
        showMessage('success', 'Igreja e admin criados com sucesso!');
      }

      // Resetar form e recarregar
      setShowForm(false);
      setEditingIgreja(null);
      setShowResetPassword(false);
      setNovaSenha('');
      setConfirmarSenha('');
      setAdminInfo(null);
      setFormData({
        nome: '',
        cnpj: '',
        presidente: '',
        pastor: '',
        superintendente_ebd: '',
        endereco: '',
        telefone: '',
        email: '',
        plano: 'trial',
        valor_mensal: 0,
        dia_vencimento: '',
        admin_nome: '',
        admin_email: '',
        admin_senha: ''
      });
      loadIgrejas();

    } catch (error) {
      console.error('Erro ao salvar igreja:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar igreja';
      showMessage('error', errorMsg);
    }
  };

  const handleEdit = async (igreja) => {
    setEditingIgreja(igreja);
    setFormData({
      nome: igreja.nome || '',
      cnpj: igreja.cnpj || '',
      presidente: igreja.presidente || '',
      pastor: igreja.pastor || '',
      superintendente_ebd: igreja.superintendente_ebd || '',
      endereco: igreja.endereco || '',
      telefone: igreja.telefone || '',
      email: igreja.email || '',
      plano: igreja.plano || 'trial',
      valor_mensal: igreja.valor_mensal || 0,
      dia_vencimento: igreja.dia_vencimento || '',
      admin_nome: '',
      admin_email: '',
      admin_senha: ''
    });
    setShowResetPassword(false);
    setNovaSenha('');
    setConfirmarSenha('');
    setAdminInfo(null);
    // Limpar estados de logo
    setLogoFile(null);
    setLogoPreview(null);
    
    // Buscar dados do admin da igreja
    try {
      const response = await axios.get(`${API}/igrejas/${igreja.id}/admin`, { headers: getAuthHeaders() });
      setAdminInfo(response.data);
    } catch (error) {
      console.error('Erro ao buscar admin da igreja:', error);
      setAdminInfo(null);
    }
    
    setShowForm(true);
  };

  const handleDelete = async (igreja) => {
    if (!window.confirm(`Desativar igreja "${igreja.nome}"? A igreja ficará oculta mas poderá ser reativada depois.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/igrejas/${igreja.id}`, { headers: getAuthHeaders() });
      showMessage('success', 'Igreja desativada com sucesso');
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao desativar igreja:', error);
      showMessage('error', 'Erro ao desativar igreja');
    }
  };

  const handleReativar = async (igreja) => {
    if (!window.confirm(`Reativar igreja "${igreja.nome}"?`)) {
      return;
    }

    try {
      await axios.post(`${API}/igrejas/${igreja.id}/reativar`, {}, { headers: getAuthHeaders() });
      showMessage('success', 'Igreja reativada com sucesso');
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao reativar igreja:', error);
      showMessage('error', 'Erro ao reativar igreja');
    }
  };

  // Função para gerar backup de uma igreja específica
  const handleBackup = async (igreja) => {
    setBackupLoading(prev => ({ ...prev, [igreja.id]: true }));
    try {
      // Criar headers com o igreja_id específico
      const headers = {
        ...getAuthHeaders(),
        'X-Igreja-ID': igreja.id
      };
      
      const response = await axios.get(`${API}/backup/generate`, { headers });
      
      // Criar arquivo para download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dataAtual = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const nomeArquivo = `backup_${igreja.nome.replace(/[^a-zA-Z0-9]/g, '_')}_${dataAtual}.json`;
      a.download = nomeArquivo;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showMessage('success', `Backup da igreja "${igreja.nome}" gerado com sucesso!`);
    } catch (error) {
      console.error('Erro ao gerar backup:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao gerar backup';
      showMessage('error', errorMsg);
    } finally {
      setBackupLoading(prev => ({ ...prev, [igreja.id]: false }));
    }
  };

  // Função para upload de logo
  const handleLogoUpload = async () => {
    if (!logoFile || !editingIgreja) {
      showMessage('error', 'Selecione uma imagem');
      return;
    }

    setLogoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      const response = await axios.post(
        `${API}/igrejas/${editingIgreja.id}/logo`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      showMessage('success', 'Logo atualizado com sucesso!');
      setLogoFile(null);
      setLogoPreview(null);
      
      // Atualizar a igreja editada com a nova URL do logo
      setEditingIgreja({ ...editingIgreja, logo_url: response.data.logo_url });
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer upload do logo';
      showMessage('error', errorMsg);
    } finally {
      setLogoLoading(false);
    }
  };

  // Função para remover logo
  const handleLogoRemove = async () => {
    if (!editingIgreja) return;
    
    if (!window.confirm('Deseja remover o logo desta igreja?')) return;

    setLogoLoading(true);
    try {
      await axios.delete(`${API}/igrejas/${editingIgreja.id}/logo`, { headers: getAuthHeaders() });
      showMessage('success', 'Logo removido com sucesso!');
      setEditingIgreja({ ...editingIgreja, logo_url: null });
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao remover logo:', error);
      showMessage('error', 'Erro ao remover logo');
    } finally {
      setLogoLoading(false);
    }
  };

  // Função para preview do logo
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para restaurar backup de uma igreja
  const handleRestore = async () => {
    if (!restoreFile || !restoreIgreja) {
      showMessage('error', 'Selecione um arquivo de backup');
      return;
    }

    if (!window.confirm(`ATENÇÃO: Isso irá SUBSTITUIR todos os dados da igreja "${restoreIgreja.nome}" pelo backup selecionado. Deseja continuar?`)) {
      return;
    }

    setRestoreLoading(true);
    try {
      // Ler o arquivo
      const fileContent = await restoreFile.text();
      const backupData = JSON.parse(fileContent);
      
      // Criar headers com o igreja_id específico
      const headers = {
        ...getAuthHeaders(),
        'X-Igreja-ID': restoreIgreja.id
      };
      
      await axios.post(`${API}/backup/restore`, backupData, { headers });
      
      showMessage('success', `Backup restaurado com sucesso na igreja "${restoreIgreja.nome}"!`);
      setRestoreIgreja(null);
      setRestoreFile(null);
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao restaurar backup:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao restaurar backup';
      showMessage('error', errorMsg);
    } finally {
      setRestoreLoading(false);
    }
  };

  // ===== ENVIO DE EMAIL =====
  const [emailMenuOpen, setEmailMenuOpen] = useState(null); // igreja_id do menu aberto
  const [emailSending, setEmailSending] = useState({});

  const handleSendTemplate = async (igreja, templateKey) => {
    setEmailMenuOpen(null);
    setEmailSending(prev => ({ ...prev, [igreja.id]: true }));
    try {
      const response = await axios.post(`${API}/email/send-template`, {
        igreja_id: igreja.id,
        template: templateKey
      }, { headers: getAuthHeaders() });
      showMessage('success', `Email enviado para ${response.data.email_destino} (${response.data.igreja})`);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Erro ao enviar email');
    }
    setEmailSending(prev => ({ ...prev, [igreja.id]: false }));
  };

  const EMAIL_TEMPLATES_OPTIONS = [
    { key: 'trial_welcome', label: 'Boas-vindas Trial', icon: '🎉' },
    { key: 'trial_expiring', label: 'Trial Expirando', icon: '⏳' },
    { key: 'trial_not_logged_24h', label: 'Lembrete 24h', icon: '📩' },
    { key: 'trial_not_logged_72h', label: 'Lembrete 72h', icon: '📩' },
  ];

  // ===== TRIAL NOT LOGGED =====
  const [trialNotLoggedLoading, setTrialNotLoggedLoading] = useState(false);

  const handleTrialNotLogged = async () => {
    setTrialNotLoggedLoading(true);
    try {
      const response = await axios.post(`${API}/trial/check-not-logged`, {}, { headers: getAuthHeaders() });
      const data = response.data;
      if (data.emails_enviados > 0) {
        const detalhes = data.detalhes.map(d => `${d.igreja} (${d.email} - ${d.tipo_lembrete})`).join(', ');
        showMessage('success', `${data.emails_enviados} email(s) enviado(s): ${detalhes}`);
      } else {
        showMessage('success', 'Nenhum trial pendente de lembrete no momento.');
      }
    } catch (error) {
      showMessage('error', 'Erro ao verificar trials: ' + (error.response?.data?.detail || error.message));
    }
    setTrialNotLoggedLoading(false);
  };

  // ===== BACKUP GLOBAL =====

  // Gerar backup global e baixar
  const handleCleanup = async () => {
    if (!window.confirm('Executar limpeza de sessões expiradas, tokens antigos e logs > 90 dias?')) return;
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${API}/cleanup/sessions`, {}, { headers: getAuthHeaders() });
      const data = response.data;
      setCleanupStats(data);
      setMessage({ type: 'success', text: `Limpeza concluída! ${data.sessions_removed} sessões, ${data.password_resets_removed} tokens e ${data.old_logs_removed} logs removidos.` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao executar limpeza: ' + (error.response?.data?.detail || error.message) });
    }
    setCleanupLoading(false);
  };

  const handleGlobalBackup = async () => {
    setGlobalBackupLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/backup/global`, { headers });

      // Decodificar base64 e criar arquivo para download
      const byteCharacters = atob(response.data.backup_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/gzip' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      const s = response.data.summary;
      showMessage('success', `Backup global gerado! ${s.igrejas} igrejas, ${s.students} alunos, ${s.attendance} chamadas (${response.data.size_compressed_mb} MB)`);
    } catch (error) {
      console.error('Erro ao gerar backup global:', error);
      showMessage('error', error.response?.data?.detail || 'Erro ao gerar backup global');
    } finally {
      setGlobalBackupLoading(false);
    }
  };

  // Gerar backup global e enviar por email
  const handleGlobalBackupEmail = async () => {
    setGlobalBackupEmailLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/backup/global?enviar_email=true&email_destino=marciosfbrk@gmail.com`, { headers });

      if (response.data.email_enviado) {
        showMessage('success', `Backup global enviado por email para ${response.data.email_destino}! (${response.data.size_compressed_mb} MB)`);
      } else {
        showMessage('error', 'Backup gerado mas houve erro ao enviar o email. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao enviar backup por email:', error);
      showMessage('error', error.response?.data?.detail || 'Erro ao enviar backup por email');
    } finally {
      setGlobalBackupEmailLoading(false);
    }
  };

  // Carregar arquivo de backup global para preview
  const handleGlobalRestoreFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setGlobalRestoreFile(file);
    setGlobalRestorePreview(null);
    setGlobalRestoreSelected({});

    try {
      // Ler o arquivo .json.gz
      const arrayBuffer = await file.arrayBuffer();

      // Converter para base64
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      // Enviar para preview
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/backup/global/preview`, {
        backup_base64: base64
      }, { headers });

      setGlobalRestorePreview(response.data);
      // Selecionar todas por default
      const selected = {};
      response.data.igrejas.forEach(ig => { selected[ig.id] = true; });
      setGlobalRestoreSelected(selected);
    } catch (error) {
      console.error('Erro ao ler backup:', error);
      showMessage('error', error.response?.data?.detail || 'Erro ao ler arquivo de backup. Verifique se é um backup global (.json.gz)');
      setGlobalRestoreFile(null);
    }
  };

  // Restaurar backup global
  const handleGlobalRestore = async () => {
    if (!globalRestoreFile || !globalRestorePreview) return;

    const igrejasIds = Object.keys(globalRestoreSelected).filter(id => globalRestoreSelected[id]);
    if (igrejasIds.length === 0) {
      showMessage('error', 'Selecione pelo menos uma igreja para restaurar');
      return;
    }

    const nomesIgrejas = globalRestorePreview.igrejas
      .filter(ig => igrejasIds.includes(ig.id))
      .map(ig => ig.nome)
      .join(', ');

    if (!window.confirm(`ATENÇÃO: Isso vai SUBSTITUIR os dados de ${igrejasIds.length} igreja(s): ${nomesIgrejas}.\n\nDeseja continuar?`)) {
      return;
    }

    setGlobalRestoreLoading(true);
    try {
      const arrayBuffer = await globalRestoreFile.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      const headers = getAuthHeaders();
      const allSelected = igrejasIds.length === globalRestorePreview.igrejas.length;

      await axios.post(`${API}/backup/global/restore`, {
        backup_base64: base64,
        igrejas_selecionadas: allSelected ? null : igrejasIds
      }, { headers });

      showMessage('success', `Backup restaurado com sucesso! ${igrejasIds.length} igreja(s) restaurada(s).`);
      setShowGlobalRestore(false);
      setGlobalRestoreFile(null);
      setGlobalRestorePreview(null);
      setGlobalRestoreSelected({});
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao restaurar backup global:', error);
      showMessage('error', error.response?.data?.detail || 'Erro ao restaurar backup global');
    } finally {
      setGlobalRestoreLoading(false);
    }
  };

  // Calcular métricas de trial
  const getTrialDaysLeft = (igreja) => {
    if (!igreja.trial_expira_em) return null;
    const expira = new Date(igreja.trial_expira_em);
    const now = new Date();
    return Math.ceil((expira - now) / (1000 * 60 * 60 * 24));
  };

  // Igrejas e filtros agora vêm pré-processados do backend (/igrejas/listar)
  const igrejasFiltradas = igrejas;

  const handleConvertToSubscriber = async (igreja) => {
    const valorStr = window.prompt(`Converter "${igreja.nome}" para assinante.\n\nDigite o valor mensal (R$):`, '');
    if (valorStr === null) return;

    const valor = parseFloat(valorStr.replace(',', '.'));
    if (isNaN(valor) || valor < 0) {
      showMessage('error', 'Valor inválido');
      return;
    }

    try {
      await axios.put(
        `${API}/igrejas/${igreja.id}`,
        {
          tipo_conta: 'paid',
          valor_mensal: valor,
          trial_expira_em: null
        },
        { headers: getAuthHeaders() }
      );
      showMessage('success', `Igreja "${igreja.nome}" convertida para assinante! Valor: R$ ${valor.toFixed(2)}/mês`);
      loadIgrejas();
    } catch (error) {
      console.error('Erro ao converter igreja:', error);
      showMessage('error', 'Erro ao converter igreja para assinante');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando igrejas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header com Gradiente */}
      <div className="bg-gradient-to-r from-indigo-900 via-blue-800 to-indigo-900 text-white px-6 py-5 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-wide">Painel SaaS</h1>
              <p className="text-blue-200 text-sm mt-1">Gerenciamento de Igrejas</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentView('contas-receber')}
                className="px-3 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all text-sm font-medium"
              >
                💳 Contas
              </button>
              <button
                onClick={() => setCurrentView('dashboard-admin')}
                className="px-3 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all text-sm font-medium"
              >
                📊 Métricas
              </button>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="px-3 py-2 bg-red-500 bg-opacity-80 rounded-lg hover:bg-opacity-100 transition-all text-sm font-medium"
                >
                  Sair
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Barra de Ações */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => {
              setEditingIgreja(null);
              setShowResetPassword(false);
              setNovaSenha('');
              setConfirmarSenha('');
              setAdminInfo(null);
              setFormData({
                nome: '',
                cnpj: '',
                presidente: '',
                pastor: '',
                superintendente_ebd: '',
                endereco: '',
                telefone: '',
                email: '',
                plano: 'trial',
                valor_mensal: 0,
                admin_nome: '',
                admin_email: '',
                admin_senha: ''
              });
              setShowForm(true);
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 font-semibold shadow-md hover:shadow-lg transition-all text-sm"
          >
            + Nova Igreja
          </button>
          <div className="h-8 w-px bg-gray-300 mx-1 self-center"></div>
          <button
            onClick={handleGlobalBackup}
            disabled={globalBackupLoading}
            className="px-3 py-2 bg-white text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-50 font-medium disabled:opacity-50 transition-all text-sm shadow-sm"
          >
            {globalBackupLoading ? 'Gerando...' : '💾 Backup'}
          </button>
          <button
            onClick={handleGlobalBackupEmail}
            disabled={globalBackupEmailLoading}
            className="px-3 py-2 bg-white text-purple-700 border border-purple-200 rounded-xl hover:bg-purple-50 font-medium disabled:opacity-50 transition-all text-sm shadow-sm"
          >
            {globalBackupEmailLoading ? 'Enviando...' : '📧 Email'}
          </button>
          <button
            onClick={() => setShowGlobalRestore(true)}
            className="px-3 py-2 bg-white text-orange-700 border border-orange-200 rounded-xl hover:bg-orange-50 font-medium transition-all text-sm shadow-sm"
          >
            📥 Restaurar
          </button>
          <button
            onClick={handleCleanup}
            disabled={cleanupLoading}
            className="px-3 py-2 bg-white text-red-700 border border-red-200 rounded-xl hover:bg-red-50 font-medium disabled:opacity-50 transition-all text-sm shadow-sm"
          >
            {cleanupLoading ? 'Limpando...' : '🧹 Limpar'}
          </button>
          <button
            onClick={handleTrialNotLogged}
            disabled={trialNotLoggedLoading}
            className="px-3 py-2 bg-white text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-50 font-medium disabled:opacity-50 transition-all text-sm shadow-sm"
          >
            {trialNotLoggedLoading ? 'Verificando...' : '📩 Lembrete Trial'}
          </button>
          <button
            onClick={() => setCurrentView('config-email-templates')}
            className="px-3 py-2 bg-white text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-50 font-medium transition-all text-sm shadow-sm"
          >
            ✉️ Config Emails
          </button>
        </div>

        {/* Mensagem */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl shadow-sm border ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Modal de Restore */}
        {restoreIgreja && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                📥 Restaurar Backup
              </h3>
              
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Igreja:</span> {restoreIgreja.nome}
                </p>
              </div>
              
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>ATENÇÃO:</strong> Esta ação irá substituir TODOS os dados atuais da igreja pelo backup selecionado. Esta ação não pode ser desfeita!
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione o arquivo de backup (.json)
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setRestoreFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {restoreFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Arquivo selecionado: {restoreFile.name}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setRestoreIgreja(null);
                    setRestoreFile(null);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  disabled={restoreLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRestore}
                  disabled={!restoreFile || restoreLoading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {restoreLoading ? 'Restaurando...' : 'Restaurar Backup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Restore Global */}
        {showGlobalRestore && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                📥 Restaurar Backup Global
              </h3>

              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>ATENÇÃO:</strong> Esta ação ira substituir os dados das igrejas selecionadas pelo conteudo do backup. Esta ação não pode ser desfeita!
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione o arquivo de backup global (.json.gz)
                </label>
                <input
                  type="file"
                  accept=".gz,.json.gz"
                  onChange={handleGlobalRestoreFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Preview do backup */}
              {globalRestorePreview && (
                <div className="mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                    <p className="text-sm text-gray-700">
                      <strong>Data do backup:</strong> {new Date(globalRestorePreview.metadata.backup_date).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Total:</strong> {globalRestorePreview.metadata.total_records?.toLocaleString()} registros em {globalRestorePreview.igrejas.length} igrejas
                    </p>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-semibold text-gray-700">Selecione as igrejas para restaurar:</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const all = {};
                          globalRestorePreview.igrejas.forEach(ig => { all[ig.id] = true; });
                          setGlobalRestoreSelected(all);
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Selecionar todas
                      </button>
                      <button
                        onClick={() => setGlobalRestoreSelected({})}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {globalRestorePreview.igrejas.map(ig => (
                      <label
                        key={ig.id}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                          globalRestoreSelected[ig.id]
                            ? 'bg-indigo-50 border-indigo-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!globalRestoreSelected[ig.id]}
                          onChange={(e) => setGlobalRestoreSelected(prev => ({
                            ...prev,
                            [ig.id]: e.target.checked
                          }))}
                          className="mr-3 h-4 w-4 text-indigo-600"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-800">{ig.nome}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              ig.tipo_conta === 'trial' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {ig.tipo_conta}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {ig.users} usuarios | {ig.turmas} turmas | {ig.alunos} alunos | {ig.presencas} chamadas
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowGlobalRestore(false);
                    setGlobalRestoreFile(null);
                    setGlobalRestorePreview(null);
                    setGlobalRestoreSelected({});
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  disabled={globalRestoreLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGlobalRestore}
                  disabled={!globalRestorePreview || globalRestoreLoading || Object.values(globalRestoreSelected).filter(Boolean).length === 0}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {globalRestoreLoading
                    ? 'Restaurando...'
                    : `Restaurar ${Object.values(globalRestoreSelected).filter(Boolean).length} igreja(s)`
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Formulário */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-sm">
                {editingIgreja ? '✏️' : '+'}
              </span>
              {editingIgreja ? 'Editar Igreja' : 'Nova Igreja'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dados da Igreja */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Dados da Igreja</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Igreja *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Assembleia de Deus - Centro"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CNPJ</label>
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="00.000.000/0001-00 (opcional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Presidente *</label>
                    <input
                      type="text"
                      name="presidente"
                      value={formData.presidente}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pastor Local *</label>
                    <input
                      type="text"
                      name="pastor"
                      value={formData.pastor}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Superintendente EBD *</label>
                    <input
                      type="text"
                      name="superintendente_ebd"
                      value={formData.superintendente_ebd}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plano *</label>
                    <select
                      name="plano"
                      value={formData.plano}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          plano: e.target.value,
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {PLANOS_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valor Mensal (R$) *</label>
                    <input
                      type="number"
                      name="valor_mensal"
                      value={formData.valor_mensal}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Defina o valor que esta igreja vai pagar mensalmente.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dia de Vencimento</label>
                    <input
                      type="number"
                      name="dia_vencimento"
                      value={formData.dia_vencimento}
                      onChange={handleInputChange}
                      min="1"
                      max="31"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: 10"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Dia do mes para cobranca (1-31). Se nao pagar, fica em atraso.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      name="endereco"
                      value={formData.endereco}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                    <input
                      type="text"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="(00) 0000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Dados do Admin (apenas ao criar) */}
              {!editingIgreja && (
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Criar Admin da Igreja</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    O admin será criado automaticamente com acesso total à igreja.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Admin *</label>
                      <input
                        type="text"
                        name="admin_nome"
                        value={formData.admin_nome}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email do Admin *</label>
                      <input
                        type="email"
                        name="admin_email"
                        value={formData.admin_email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Senha Inicial do Admin *</label>
                      <input
                        type="password"
                        name="admin_senha"
                        value={formData.admin_senha}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Mínimo 6 caracteres"
                        minLength="6"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Esta senha será enviada ao admin da igreja.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Resetar Senha do Admin (apenas ao editar) */}
              {editingIgreja && (
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Resetar Senha do Admin</h3>
                  {adminInfo && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Admin:</span> {adminInfo.admin_nome}
                      </p>
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Email:</span> {adminInfo.admin_email}
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mb-4">
                    Use esta opção apenas se o admin da igreja perdeu a senha.
                  </p>

                  {!showResetPassword ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNovaSenha('');
                        setConfirmarSenha('');
                        setShowResetPassword(true);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Resetar Senha do Admin
                    </button>
                  ) : (
                    <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nova Senha *</label>
                          <input
                            type="password"
                            value={novaSenha}
                            onChange={(e) => setNovaSenha(e.target.value)}
                            minLength="6"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Mínimo 6 caracteres"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Senha *</label>
                          <input
                            type="password"
                            value={confirmarSenha}
                            onChange={(e) => setConfirmarSenha(e.target.value)}
                            minLength="6"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Digite novamente"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowResetPassword(false);
                            setNovaSenha('');
                            setConfirmarSenha('');
                          }}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!novaSenha || novaSenha.length < 6) {
                              showMessage('error', 'Senha deve ter no mínimo 6 caracteres');
                              return;
                            }
                            if (novaSenha !== confirmarSenha) {
                              showMessage('error', 'As senhas não coincidem');
                              return;
                            }
                            if (!window.confirm(`Deseja realmente resetar a senha do admin da igreja "${editingIgreja.nome}"?`)) {
                              return;
                            }
                            try {
                              const response = await axios.post(
                                `${API}/igrejas/${editingIgreja.id}/reset-admin-password`,
                                { nova_senha: novaSenha },
                                { headers: getAuthHeaders() }
                              );
                              showMessage('success', `Senha resetada com sucesso! Email do admin: ${response.data.admin_email}`);
                              setShowResetPassword(false);
                              setNovaSenha('');
                              setConfirmarSenha('');
                            } catch (error) {
                              console.error('Erro ao resetar senha:', error);
                              const errorMsg = error.response?.data?.detail || 'Erro ao resetar senha';
                              showMessage('error', errorMsg);
                            }
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                          Confirmar Reset
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Upload de Logo (apenas ao editar) */}
              {editingIgreja && (
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">📷 Logo da Igreja</h3>
                  
                  <div className="flex items-start space-x-6">
                    {/* Preview do logo atual ou novo */}
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : editingIgreja.logo_url ? (
                          <img src={editingIgreja.logo_url} alt="Logo atual" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-4xl text-gray-400">🏛️</span>
                        )}
                      </div>
                      {editingIgreja.logo_url && !logoPreview && (
                        <p className="text-xs text-green-600 mt-1 text-center">Logo atual</p>
                      )}
                    </div>
                    
                    {/* Controles de upload */}
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-3">
                        O logo aparecerá no Dashboard da igreja. Formatos: JPG, PNG, GIF, WEBP. Máximo: 2MB.
                      </p>
                      
                      <div className="space-y-3">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleLogoFileChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        
                        <div className="flex space-x-2">
                          {logoFile && (
                            <button
                              type="button"
                              onClick={handleLogoUpload}
                              disabled={logoLoading}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                            >
                              {logoLoading ? 'Enviando...' : '✓ Salvar Logo'}
                            </button>
                          )}
                          
                          {editingIgreja.logo_url && (
                            <button
                              type="button"
                              onClick={handleLogoRemove}
                              disabled={logoLoading}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                            >
                              🗑️ Remover Logo
                            </button>
                          )}
                          
                          {logoPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                              }}
                              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Botões */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingIgreja(null);
                    setShowResetPassword(false);
                    setNovaSenha('');
                    setConfirmarSenha('');
                    setAdminInfo(null);
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {editingIgreja ? 'Salvar Alterações' : 'Criar Igreja e Admin'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Barra de busca + filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-1 gap-2 min-w-0">
            <input
              type="text"
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setBusca(buscaInput); } }}
              placeholder="Buscar por nome, CNPJ ou email…"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
            />
            <button
              onClick={() => { setPage(1); setBusca(buscaInput); }}
              className="px-4 py-2 text-sm font-semibold bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Buscar
            </button>
            {busca && (
              <button
                onClick={() => { setBuscaInput(''); setBusca(''); setPage(1); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Limpar
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { value: 'ativas_trial', label: 'Em uso' },
              { value: 'ativas', label: 'Ativas' },
              { value: 'trial', label: 'Trial' },
              { value: 'atraso', label: 'Em atraso' },
              { value: 'desativadas', label: 'Desativadas' },
              { value: 'todas', label: 'Todas' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => { setFiltroStatus(f.value); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                  filtroStatus === f.value
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3 text-sm text-gray-600">
          {pageInfo.total} igreja(s) encontrada(s) — página {page} de {pageInfo.pages || 1}
        </div>

        {igrejasFiltradas.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <p className="text-gray-600 text-lg">Nenhuma igreja encontrada com esse filtro.</p>
            <p className="text-gray-500 text-sm mt-2">Tente outro filtro ou limpe a busca.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <tr>
                    <SortableTh field="codigo" label="#" current={sortField} dir={sortDir} onSort={(f) => { setSortField(f); setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }} className="w-12" />
                    <SortableTh field="nome" label="Igreja" current={sortField} dir={sortDir} onSort={(f) => { setSortField(f); setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }} />
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Plano</th>
                    <SortableTh field="total_alunos" label="Alunos" current={sortField} dir={sortDir} onSort={(f) => { setSortField(f); setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }} align="right" />
                    <SortableTh field="ultimo_login" label="Último login" current={sortField} dir={sortDir} onSort={(f) => { setSortField(f); setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }} />
                    <SortableTh field="valor_mensal" label="R$/mês" current={sortField} dir={sortDir} onSort={(f) => { setSortField(f); setSortDir(sortField === f && sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }} align="right" />
                    <th className="px-3 py-2 text-left font-semibold">Vencimento</th>
                    <th className="px-3 py-2 text-right font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {igrejasFiltradas.map((igreja) => {
                    const daysLeft = getTrialDaysLeft(igreja);
                    const isExpired = igreja.trial_expirado;
                    const emAtraso = igreja.em_atraso;
                    const status = !igreja.ativa
                      ? { bg: 'bg-gray-100 text-gray-600', text: 'Desativada' }
                      : isExpired
                      ? { bg: 'bg-red-100 text-red-700', text: 'Trial expirado' }
                      : emAtraso
                      ? { bg: 'bg-red-100 text-red-700', text: 'Em atraso' }
                      : igreja.tipo_conta === 'trial'
                      ? { bg: 'bg-amber-100 text-amber-700', text: `Trial (${daysLeft}d)` }
                      : { bg: 'bg-emerald-100 text-emerald-700', text: 'Ativa' };

                    return (
                      <tr key={igreja.id} className={`hover:bg-blue-50/40 transition-colors ${!igreja.ativa ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-2 text-gray-500 text-xs">{igreja.codigo || '-'}</td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-gray-900 truncate max-w-[18rem]">{igreja.nome}</div>
                          {igreja.email && <div className="text-xs text-gray-500 truncate max-w-[18rem]">{igreja.email}</div>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${status.bg}`}>{status.text}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-700 text-xs">{igreja.plano || '-'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-800">{igreja.total_alunos || 0}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {igreja.ultimo_login
                            ? new Date(igreja.ultimo_login).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : <span className="text-gray-400">nunca</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                          {typeof igreja.valor_mensal === 'number' ? igreja.valor_mensal.toFixed(2).replace('.', ',') : '0,00'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {igreja.dia_vencimento ? `dia ${igreja.dia_vencimento}` : (igreja.plano_ativo_ate ? new Date(igreja.plano_ativo_ate).toLocaleDateString('pt-BR') : '-')}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handleEdit(igreja)}
                              className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                              title="Editar"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleBackup(igreja)}
                              disabled={backupLoading[igreja.id]}
                              className="px-2 py-1 text-xs font-medium bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                              title="Backup"
                            >
                              Backup
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => setEmailMenuOpen(emailMenuOpen === igreja.id ? null : igreja.id)}
                                disabled={emailSending[igreja.id]}
                                className="px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50"
                                title="Email"
                              >
                                {emailSending[igreja.id] ? '...' : 'Email'}
                              </button>
                              {emailMenuOpen === igreja.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[180px]">
                                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 border-b">Enviar template:</p>
                                  {EMAIL_TEMPLATES_OPTIONS.map(t => (
                                    <button
                                      key={t.key}
                                      onClick={() => handleSendTemplate(igreja, t.key)}
                                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
                                    >
                                      {t.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {igreja.ativa ? (
                              <button
                                onClick={() => handleDelete(igreja)}
                                className="px-2 py-1 text-xs font-medium bg-red-50 text-red-700 rounded hover:bg-red-100"
                                title="Desativar"
                              >
                                Desativar
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReativar(igreja)}
                                className="px-2 py-1 text-xs font-medium bg-green-50 text-green-700 rounded hover:bg-green-100"
                                title="Reativar"
                              >
                                Reativar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-xs text-gray-600">
                Mostrando {(page - 1) * pageInfo.limit + 1}–{Math.min(page * pageInfo.limit, pageInfo.total)} de {pageInfo.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1.5 text-xs text-gray-600">{page} / {pageInfo.pages || 1}</span>
                <button
                  onClick={() => setPage(Math.min(pageInfo.pages || 1, page + 1))}
                  disabled={page >= (pageInfo.pages || 1)}
                  className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GerenciarIgrejas;
